import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Button,
  Stack,
  Box,
  Typography,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PostAddRoundedIcon from "@mui/icons-material/PostAddRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import config from "../../config";

const BRAND_GRADIENT = "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)";
const LINE = "1px solid rgba(15,23,42,0.08)";
const PRICE_COLS = "104px repeat(3, minmax(0, 1fr))";
const BUYER_COLS = { xs: "minmax(0,1fr) 92px 34px", sm: "minmax(0,1fr) 130px 40px" };
const NUMERIC_KEYS = ["total", "handQty", "handLo", "handHi", "combQty", "combLo", "combHi"];

let uidCounter = 0;
const nextUid = () => `a${++uidCounter}`;

const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};
const isInvalidNumber = (v) => {
  if (v === "" || v === null || v === undefined) return false; 
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isNaN(n) || n < 0;
};
const byLabel = (a, b) => a.label.localeCompare(b.label);

const makeBuyer = () => ({ uid: nextUid(), buyerId: "", name: "", bags: "" });
const makeVariety = () => ({
  uid: nextUid(),
  varietyId: "",
  varietyName: "",
  total: "",
  handQty: "",
  handLo: "",
  handHi: "",
  combQty: "",
  combLo: "",
  combHi: "",
  buyers: [],
});

const buildInitialForm = (defaultDate) => {
  const parsed = defaultDate ? dayjs(defaultDate) : null;
  const usable = parsed && parsed.isValid() && !parsed.isAfter(dayjs(), "day");
  return {
    date: (usable ? parsed : dayjs()).format("YYYY-MM-DD"),
    mandiId: "",
    mandiName: "",
    varieties: [makeVariety()],
  };
};

const isBlankBuyer = (b) => !b.buyerId && (b.bags === "" || toNum(b.bags) === 0);

const validate = (form) => {
  const top = {};
  if (!form.date) top.date = "Select a date";
  else if (dayjs(form.date).isAfter(dayjs(), "day")) top.date = "Date can't be in the future";
  if (!form.mandiId) top.mandi = "Select a mandi location";

  const varieties = form.varieties.map((v) => {
    const fields = {};
    if (!v.varietyId) fields.variety = "Select a variety";
    NUMERIC_KEYS.forEach((k) => {
      if (isInvalidNumber(v[k])) fields[k] = "Invalid";
    });
    if (!fields.handLo && !fields.handHi && toNum(v.handHi) < toNum(v.handLo)) fields.handHi = "High < low";
    if (!fields.combLo && !fields.combHi && toNum(v.combHi) < toNum(v.combLo)) fields.combHi = "High < low";

    const buyers = v.buyers.map((b) => {
      if (isBlankBuyer(b)) return {};
      const e = {};
      if (!b.buyerId) e.name = "Select a buyer";
      if (isInvalidNumber(b.bags)) e.bags = "Invalid";
      return e;
    });
    const hasErr = Object.keys(fields).length > 0 || buyers.some((e) => Object.keys(e).length > 0);
    return { fields, buyers, hasErr };
  });

  const isValid = Object.keys(top).length === 0 && varieties.every((v) => !v.hasErr);
  return { top, varieties, isValid };
};

const mapBuyers = (raw) =>
  raw
    .filter((b) => b?._id && b?.buyerName)
    .map((b) => ({
      _id: b._id,
      label: String(b.buyerName).trim(),
      sub: b.contactNumber && b.contactNumber !== ".." ? String(b.contactNumber) : "",
    }))
    .sort(byLabel);

const mapVarieties = (raw) =>
  raw
    .filter((v) => v?._id && v?.varietyName && v?.status !== false)
    .map((v) => ({ _id: v._id, label: String(v.varietyName).trim(), sub: v.varietyCode || "" }))
    .sort(byLabel);

const mapMandis = (raw) =>
  raw
    .filter((m) => m?._id && m?.mandiLocation && m?.isActive !== false && m?.softDelete !== true)
    .map((m) => ({
      _id: m._id,
      label: String(m.mandiLocation).trim(),
      sub: [m.mandiPartner, m.state].filter(Boolean).join(", ") + (m.mandiCode ? ` · ${m.mandiCode}` : ""),
    }))
    .sort(byLabel);

const useMasterList = (open, token, path, mapper) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(
    async (signal) => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${config.apiBaseUrl}${path}`, {
          method: "GET",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          signal,
        });
        if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
        const json = await res.json();
        if (json?.success === false) throw new Error(json?.message || "Failed to load list.");
        setItems(mapper(Array.isArray(json?.data) ? json.data : []));
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message || "Could not load list.");
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [token, path, mapper]
  );

  useEffect(() => {
    if (!open || items.length > 0) return undefined;
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [open]);

  const reload = useCallback(() => load(new AbortController().signal), [load]);
  return { items, loading, error, reload };
};


const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "8px", background: "#fff", fontSize: "0.9rem" },
  "& .MuiFormHelperText-root": { mt: 0.25, mx: 0.5, fontSize: "0.68rem" },
};

const SectionTitle = ({ children, hint }) => (
  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.25 }}>
    <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#0F172A" }}>{children}</Typography>
    {hint && <Typography sx={{ fontSize: "0.74rem", color: "rgba(15,23,42,0.5)" }}>{hint}</Typography>}
  </Stack>
);

const SubTitle = ({ children, hint }) => (
  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.75 }}>
    <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "rgba(15,23,42,0.5)" }}>
      {children}
    </Typography>
    {hint && <Typography sx={{ fontSize: "0.7rem", color: "rgba(15,23,42,0.4)" }}>{hint}</Typography>}
  </Stack>
);

const Panel = ({ children }) => (
  <Box sx={{ border: LINE, borderRadius: "12px", overflow: "hidden", background: "#fff" }}>{children}</Box>
);

const HeadCell = ({ children }) => (
  <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "rgba(15,23,42,0.5)" }}>
    {children}
  </Typography>
);

const Pill = ({ color, bg, children }) => (
  <Box sx={{ px: 1.25, py: 0.4, borderRadius: "999px", fontWeight: 700, fontSize: "0.78rem", color, background: bg }}>{children}</Box>
);

const NumField = ({ label, value, onChange, error, prefix, disabled, showLabel = true }) => (
  <TextField
    fullWidth
    size="small"
    label={showLabel ? label : undefined}
    placeholder={showLabel ? undefined : "0"}
    value={value}
    onChange={onChange}
    error={Boolean(error)}
    helperText={error || undefined}
    disabled={disabled}
    type="number"
    inputProps={{ min: 0, step: "any", inputMode: "decimal", "aria-label": label }}
    onWheel={(e) => e.target.blur()} 
    onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
    onFocus={(e) => e.target.select()}
    InputProps={{ startAdornment: prefix ? <InputAdornment position="start">{prefix}</InputAdornment> : undefined }}
    sx={fieldSx}
  />
);

const PickerField = ({
  label,
  placeholder,
  options,
  loading,
  value,
  onChange,
  error,
  disabled,
  icon,
  showLabel = true,
  isOptionDisabled,
  disabledHint = "Already added",
  autoFocus,
  noOptionsText = "No matches",
  ariaLabel,
}) => (
  <Autocomplete
    size="small"
    fullWidth
    sx={{ minWidth: 0 }}
    options={options}
    loading={loading}
    disabled={disabled}
    value={value}
    autoHighlight
    openOnFocus
    blurOnSelect
    noOptionsText={noOptionsText}
    loadingText="Loading…"
    getOptionLabel={(o) => o?.label || ""}
    isOptionEqualToValue={(o, v) => o._id === v._id}
    getOptionDisabled={isOptionDisabled}
    onChange={(_, opt) => onChange(opt)}
    ListboxProps={{ style: { maxHeight: 240 } }}
    renderOption={(props, o) => {
      const { key, ...rest } = props;
      const off = isOptionDisabled ? isOptionDisabled(o) : false;
      return (
        <li key={key} {...rest}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.86rem", fontWeight: 600 }} noWrap>
              {o.label}
            </Typography>
            <Typography sx={{ fontSize: "0.68rem", color: "rgba(15,23,42,0.5)" }}>{off ? disabledHint : o.sub || " "}</Typography>
          </Box>
        </li>
      );
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        label={showLabel ? label : undefined}
        placeholder={placeholder}
        autoFocus={autoFocus}
        error={Boolean(error)}
        helperText={error || undefined}
        inputProps={{ ...params.inputProps, "aria-label": ariaLabel || label }}
        InputProps={{
          ...params.InputProps,
          startAdornment: icon ? (
            <>
              <InputAdornment position="start">{icon}</InputAdornment>
              {params.InputProps.startAdornment}
            </>
          ) : (
            params.InputProps.startAdornment
          ),
          endAdornment: (
            <>
              {loading ? <CircularProgress color="inherit" size={16} /> : null}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
        sx={fieldSx}
      />
    )}
  />
);

const TabLabel = ({ name, index, hasError }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <span>{name || `Variety ${index + 1}`}</span>
    {hasError && <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#DC2626" }} />}
  </Stack>
);

const ListErrorAlert = ({ what, message, onRetry }) => (
  <Alert
    severity="error"
    sx={{ borderRadius: "10px" }}
    action={
      <Button color="inherit" size="small" startIcon={<RefreshRoundedIcon />} onClick={onRetry} sx={{ textTransform: "none" }}>
        Retry
      </Button>
    }
  >
    Couldn't load {what}. {message}
  </Alert>
);


const AddManualReportDialog = ({ open, token, defaultDate, onClose, onSaved }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [form, setForm] = useState(() => buildInitialForm(defaultDate));
  const [activeUid, setActiveUid] = useState("");
  const [focusUid, setFocusUid] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState("");
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [toast, setToast] = useState("");
  const snapshotRef = useRef(JSON.stringify(form));
  const contentRef = useRef(null);
  const listRef = useRef(null);
  const mandis = useMasterList(open, token, "/mandi-partner/get-all", mapMandis);
  const varieties = useMasterList(open, token, "/variety/get", mapVarieties);
  const buyers = useMasterList(open, token, "/buyers/buyer/get", mapBuyers);

  useEffect(() => {
    if (!open) return;
    const init = buildInitialForm(defaultDate);
    setForm(init);
    setActiveUid(init.varieties[0].uid);
    setFocusUid("");
    setSubmitted(false);
    setApiError("");
    setConfirmDiscard(false);
    snapshotRef.current = JSON.stringify(init);
  }, [open]);

  const activeIndex = Math.max(0, form.varieties.findIndex((v) => v.uid === activeUid));
  const active = form.varieties[activeIndex];
  const mandiById = useMemo(() => new Map(mandis.items.map((m) => [m._id, m])), [mandis.items]);
  const varietyById = useMemo(() => new Map(varieties.items.map((v) => [v._id, v])), [varieties.items]);
  const buyerById = useMemo(() => new Map(buyers.items.map((b) => [b._id, b])), [buyers.items]);
  const takenVarietyIds = useMemo(() => new Set(form.varieties.map((v) => v.varietyId).filter(Boolean)), [form.varieties]);
  const takenBuyerIds = useMemo(() => new Set(active.buyers.map((b) => b.buyerId).filter(Boolean)), [active.buyers]);
  const validation = useMemo(() => validate(form), [form]);
  const isDirty = JSON.stringify(form) !== snapshotRef.current;
  const marketTotal = useMemo(() => form.varieties.reduce((sum, v) => sum + toNum(v.total), 0), [form.varieties]);
  const activeBuyersTotal = useMemo(() => active.buyers.reduce((sum, b) => sum + toNum(b.bags), 0), [active.buyers]);

  const topErr = submitted ? validation.top : {};
  const activeErr = submitted ? validation.varieties[activeIndex] : { fields: {}, buyers: [], hasErr: false };

  const patchVariety = (uid, patch) =>
    setForm((f) => ({ ...f, varieties: f.varieties.map((v) => (v.uid === uid ? { ...v, ...patch } : v)) }));
  const setVField = (key) => (e) => patchVariety(active.uid, { [key]: e.target.value });

  const addVariety = () => {
    const v = makeVariety();
    setForm((f) => ({ ...f, varieties: [...f.varieties, v] }));
    setActiveUid(v.uid);
    setFocusUid(v.uid);
  };
  const removeVariety = (uid) => {
    if (form.varieties.length <= 1) return;
    const idx = form.varieties.findIndex((v) => v.uid === uid);
    const next = form.varieties.filter((v) => v.uid !== uid);
    setForm((f) => ({ ...f, varieties: next }));
    setActiveUid(next[Math.max(0, idx - 1)].uid);
    setFocusUid("");
  };

  const patchBuyer = (vUid, bUid, patch) =>
    setForm((f) => ({
      ...f,
      varieties: f.varieties.map((v) =>
        v.uid === vUid ? { ...v, buyers: v.buyers.map((b) => (b.uid === bUid ? { ...b, ...patch } : b)) } : v
      ),
    }));
  const addBuyer = () => {
    const b = makeBuyer();
    setForm((f) => ({ ...f, varieties: f.varieties.map((v) => (v.uid === active.uid ? { ...v, buyers: [...v.buyers, b] } : v)) }));
    setFocusUid(b.uid);
    requestAnimationFrame(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }));
  };
  const removeBuyer = (bUid) =>
    setForm((f) => ({
      ...f,
      varieties: f.varieties.map((v) => (v.uid === active.uid ? { ...v, buyers: v.buyers.filter((b) => b.uid !== bUid) } : v)),
    }));

  const requestClose = () => {
    if (saving) return;
    if (isDirty) setConfirmDiscard(true);
    else onClose();
  };

  const handleSave = async () => {
    setSubmitted(true);
    setApiError("");

    if (!validation.isValid) {
      const badIdx = validation.varieties.findIndex((v) => v.hasErr);
      if (badIdx >= 0) setActiveUid(form.varieties[badIdx].uid);
      contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const payload = {
      mandiPartnerName: form.mandiId, 
      date: form.date,
      marketTotal, 
      varieties: form.varieties.map((v) => ({
        varietyId: v.varietyId,
        variety: v.varietyName,
        total: toNum(v.total),
        handQty: toNum(v.handQty),
        handLo: toNum(v.handLo),
        handHi: toNum(v.handHi),
        combQty: toNum(v.combQty),
        combLo: toNum(v.combLo),
        combHi: toNum(v.combHi),
        buyers: v.buyers
          .filter((b) => b.buyerId)
          .map((b) => ({ buyerId: b.buyerId, name: b.name, bags: toNum(b.bags) })),
      })),
    };

    setSaving(true);
    try {
      const res = await fetch(`${config.apiBaseUrl}/campaign-call/manual-ivr-form`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw new Error(json?.message || `Request failed with status ${res.status}`);
      }
      setToast("Report added successfully");
      onSaved?.();
      onClose();
    } catch (err) {
      setApiError(err.message || "Failed to add report.");
      contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSaving(false);
    }
  };

  const priceRows = [
    { key: "hand", label: "Hand", color: "#6D28D9", bg: "rgba(124,58,237,0.10)", qty: "handQty", lo: "handLo", hi: "handHi" },
    { key: "comb", label: "Combine", color: "#B45309", bg: "rgba(217,119,6,0.12)", qty: "combQty", lo: "combLo", hi: "combHi" },
  ];

  const buyersExceedTotal = toNum(active.total) > 0 && activeBuyersTotal > toNum(active.total);
  const showTopWarning = submitted && !validation.isValid;

  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose}
        fullScreen={isMobile}
        fullWidth
        maxWidth={false}
        scroll="paper"
        aria-labelledby="add-report-title"
        PaperProps={{ sx: { maxWidth: 720, borderRadius: { xs: 0, sm: "20px" } } }}
      >
        <DialogTitle
          id="add-report-title"
          component="div"
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderBottom: LINE,
            background: "linear-gradient(135deg, rgba(37,99,235,0.07) 0%, rgba(6,182,212,0.07) 100%)",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  borderRadius: "13px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  background: BRAND_GRADIENT,
                  boxShadow: "0 6px 16px rgba(37,99,235,0.25)",
                }}
              >
                <PostAddRoundedIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", lineHeight: 1.2 }}>Add report</Typography>
                <Typography sx={{ fontSize: "0.78rem", color: "rgba(15,23,42,0.55)", mt: 0.3 }}>
                  Enter a manual IVR report for one mandi
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={requestClose} disabled={saving} aria-label="Close" size="small" sx={{ background: "#fff", border: LINE, alignSelf: "flex-start" }}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent ref={contentRef} sx={{ px: { xs: 2, sm: 3 }, pt: "20px !important", pb: 2.5 }}>
          {apiError && (
            <Alert severity="error" onClose={() => setApiError("")} sx={{ mb: 2, borderRadius: "10px" }}>
              {apiError}
            </Alert>
          )}
          {showTopWarning && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: "10px" }}>
              Please fix the highlighted fields{validation.varieties.some((v) => v.hasErr) ? " (tabs marked with a red dot need attention)" : ""}.
            </Alert>
          )}

          <SectionTitle>Report details</SectionTitle>
          <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "minmax(0,1fr)", sm: "repeat(2,minmax(0,1fr))" } }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date"
                value={form.date ? dayjs(form.date) : null}
                maxDate={dayjs()}
                disabled={saving}
                onChange={(v) => setForm((f) => ({ ...f, date: v && v.isValid() ? v.format("YYYY-MM-DD") : "" }))}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: Boolean(topErr.date),
                    helperText: topErr.date || undefined,
                    sx: fieldSx,
                  },
                }}
              />
            </LocalizationProvider>

            <PickerField
              label="Mandi location"
              placeholder="Select mandi location"
              options={mandis.items}
              loading={mandis.loading}
              disabled={saving}
              value={mandiById.get(form.mandiId) || null}
              onChange={(opt) => setForm((f) => ({ ...f, mandiId: opt?._id || "", mandiName: opt?.label || "" }))}
              error={topErr.mandi}
              icon={<StorefrontRoundedIcon sx={{ fontSize: 18, color: "rgba(15,23,42,0.4)" }} />}
              noOptionsText="No matching mandi location"
            />
          </Box>
          {mandis.error && (
            <Box sx={{ mt: 1.5 }}>
              <ListErrorAlert what="mandi locations" message={mandis.error} onRetry={mandis.reload} />
            </Box>
          )}

          {/* Varieties */}
          <Box sx={{ mt: 3 }}>
            <SectionTitle hint={`${form.varieties.length} ${form.varieties.length === 1 ? "variety" : "varieties"} in this report`}>
              Varieties
            </SectionTitle>

            <Stack direction="row" alignItems="center" sx={{ borderBottom: LINE }}>
              <Tabs
                value={active.uid}
                onChange={(_, v) => {
                  setActiveUid(v);
                  setFocusUid("");
                }}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                TabIndicatorProps={{ style: { height: 3, borderRadius: 3, background: BRAND_GRADIENT } }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 42,
                  "& .MuiTab-root": { textTransform: "none", fontWeight: 700, fontSize: "0.84rem", minHeight: 42, px: 1.75 },
                }}
              >
                {form.varieties.map((v, i) => (
                  <Tab
                    key={v.uid}
                    value={v.uid}
                    label={<TabLabel name={v.varietyName} index={i} hasError={submitted && validation.varieties[i]?.hasErr} />}
                  />
                ))}
              </Tabs>
              <Button
                size="small"
                startIcon={<AddRoundedIcon />}
                onClick={addVariety}
                disabled={saving}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px", flexShrink: 0, ml: 0.5 }}
              >
                Add variety
              </Button>
            </Stack>

            <Box key={active.uid} sx={{ pt: 2 }}>
              <Box
                sx={{
                  display: "grid",
                  gap: 1.5,
                  alignItems: "start",
                  gridTemplateColumns: { xs: "minmax(0,1fr) 40px", sm: "minmax(0,1fr) 170px 40px" },
                }}
              >
                <PickerField
                  label="Variety"
                  placeholder="Select variety"
                  options={varieties.items}
                  loading={varieties.loading}
                  disabled={saving}
                  value={varietyById.get(active.varietyId) || null}
                  autoFocus={focusUid === active.uid}
                  onChange={(opt) => patchVariety(active.uid, { varietyId: opt?._id || "", varietyName: opt?.label || "" })}
                  isOptionDisabled={(o) => takenVarietyIds.has(o._id) && o._id !== active.varietyId}
                  disabledHint="Used in another tab"
                  error={activeErr.fields.variety}
                  icon={<CategoryRoundedIcon sx={{ fontSize: 18, color: "rgba(15,23,42,0.4)" }} />}
                  noOptionsText="No matching variety"
                />
                <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" }, gridRow: { xs: 2, sm: 1 }, gridColumnStart: { sm: 2 } }}>
                  <NumField label="Total arrival" value={active.total} onChange={setVField("total")} error={activeErr.fields.total} disabled={saving} />
                </Box>
                <Box sx={{ gridRow: 1, gridColumn: { xs: 2, sm: 3 }, height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Tooltip title={form.varieties.length <= 1 ? "At least one variety is required" : "Remove this variety"}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => removeVariety(active.uid)}
                        disabled={saving || form.varieties.length <= 1}
                        aria-label="Remove this variety"
                        sx={{ color: "#B91C1C", "&:hover": { background: "rgba(220,38,38,0.08)" } }}
                      >
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>

              {varieties.error && (
                <Box sx={{ mt: 1.5 }}>
                  <ListErrorAlert what="varieties" message={varieties.error} onRetry={varieties.reload} />
                </Box>
              )}

              <Box sx={{ mt: 2.5 }}>
                <SubTitle hint="Quantity in bags, price in ₹">Quantity & pricing</SubTitle>
                <Panel>
                  <Box
                    sx={{
                      display: { xs: "none", sm: "grid" },
                      gridTemplateColumns: PRICE_COLS,
                      columnGap: 1.5,
                      px: 2,
                      py: 1,
                      background: "#F8FAFC",
                      borderBottom: LINE,
                    }}
                  >
                    <HeadCell>Type</HeadCell>
                    <HeadCell>Quantity</HeadCell>
                    <HeadCell>Low price</HeadCell>
                    <HeadCell>High price</HeadCell>
                  </Box>
                  {priceRows.map((r, idx) => (
                    <Box
                      key={r.key}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "repeat(3,minmax(0,1fr))", sm: PRICE_COLS },
                        columnGap: 1.5,
                        rowGap: 1,
                        alignItems: "start",
                        px: 2,
                        py: 1.5,
                        borderBottom: idx < priceRows.length - 1 ? LINE : "none",
                      }}
                    >
                      <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" }, display: "flex", alignItems: "center", height: { sm: 40 } }}>
                        <Pill color={r.color} bg={r.bg}>{r.label}</Pill>
                      </Box>
                      <NumField label="Quantity" showLabel={isMobile} value={active[r.qty]} onChange={setVField(r.qty)} error={activeErr.fields[r.qty]} disabled={saving} />
                      <NumField label="Low price" showLabel={isMobile} prefix="₹" value={active[r.lo]} onChange={setVField(r.lo)} error={activeErr.fields[r.lo]} disabled={saving} />
                      <NumField label="High price" showLabel={isMobile} prefix="₹" value={active[r.hi]} onChange={setVField(r.hi)} error={activeErr.fields[r.hi]} disabled={saving} />
                    </Box>
                  ))}
                </Panel>
              </Box>

              <Box sx={{ mt: 2.5 }}>
                <SubTitle hint={buyers.loading ? "Loading buyer list…" : "Select from your buyer list"}>Buyers</SubTitle>
                {buyers.error && (
                  <Box sx={{ mb: 1.25 }}>
                    <ListErrorAlert what="buyers" message={buyers.error} onRetry={buyers.reload} />
                  </Box>
                )}
                <Panel>
                  <Box
                    sx={{
                      display: { xs: "none", sm: "grid" },
                      gridTemplateColumns: BUYER_COLS,
                      columnGap: 1.5,
                      px: 2,
                      py: 1,
                      background: "#F8FAFC",
                      borderBottom: LINE,
                    }}
                  >
                    <HeadCell>Buyer</HeadCell>
                    <HeadCell>Bags</HeadCell>
                    <span />
                  </Box>

                  {active.buyers.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 3, px: 2 }}>
                      <Typography sx={{ fontSize: "0.85rem", color: "rgba(15,23,42,0.5)" }}>No buyers added yet (optional)</Typography>
                    </Box>
                  ) : (
                    <Box ref={listRef} sx={{ maxHeight: { xs: "30vh", sm: 220 }, overflowY: "auto" }}>
                      {active.buyers.map((b, i) => {
                        const be = submitted ? activeErr.buyers[i] || {} : {};
                        return (
                          <Box
                            key={b.uid}
                            sx={{
                              display: "grid",
                              gridTemplateColumns: BUYER_COLS,
                              columnGap: 1.5,
                              alignItems: "start",
                              px: 2,
                              py: 1.25,
                              borderBottom: i < active.buyers.length - 1 ? LINE : "none",
                            }}
                          >
                            <PickerField
                              label={`Buyer ${i + 1}`}
                              showLabel={isMobile}
                              placeholder="Select buyer"
                              options={buyers.items}
                              loading={buyers.loading}
                              disabled={saving}
                              value={buyerById.get(b.buyerId) || null}
                              autoFocus={focusUid === b.uid}
                              onChange={(opt) => patchBuyer(active.uid, b.uid, { buyerId: opt?._id || "", name: opt?.label || "" })}
                              isOptionDisabled={(o) => takenBuyerIds.has(o._id) && o._id !== b.buyerId}
                              error={be.name}
                              noOptionsText="No matching buyer"
                            />
                            <NumField label="Bags" showLabel={isMobile} value={b.bags} onChange={(e) => patchBuyer(active.uid, b.uid, { bags: e.target.value })} error={be.bags} disabled={saving} />
                            <Box sx={{ height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Tooltip title="Remove buyer">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => removeBuyer(b.uid)}
                                    disabled={saving}
                                    aria-label={`Remove buyer ${i + 1}`}
                                    sx={{ color: "#B91C1C", "&:hover": { background: "rgba(220,38,38,0.08)" } }}
                                  >
                                    <DeleteOutlineRoundedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 0.75, background: "#F8FAFC", borderTop: LINE }}>
                    <Button
                      size="small"
                      startIcon={<AddRoundedIcon />}
                      onClick={addBuyer}
                      disabled={saving || Boolean(buyers.error)}
                      sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px" }}
                    >
                      Add buyer
                    </Button>
                    <Typography sx={{ fontSize: "0.8rem", color: "rgba(15,23,42,0.65)" }}>
                      {active.buyers.length} {active.buyers.length === 1 ? "buyer" : "buyers"} ·{" "}
                      <Box component="span" sx={{ fontWeight: 800, color: buyersExceedTotal ? "#B45309" : "#0F172A" }}>
                        {activeBuyersTotal.toLocaleString("en-IN")} bags
                      </Box>
                    </Typography>
                  </Stack>
                </Panel>
                {buyersExceedTotal && (
                  <Typography sx={{ mt: 0.75, fontSize: "0.72rem", color: "#B45309" }}>
                    Buyer bags ({activeBuyersTotal.toLocaleString("en-IN")}) are more than this variety's total arrival ({toNum(active.total).toLocaleString("en-IN")}).
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: LINE,
            px: { xs: 2, sm: 3 },
            py: 1.5,
            justifyContent: "space-between",
            gap: 1,
            "& > :not(style) ~ :not(style)": { ml: 0 },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.72rem", color: "rgba(15,23,42,0.5)", lineHeight: 1.2 }}>Market total</Typography>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, lineHeight: 1.3 }}>
              {marketTotal.toLocaleString("en-IN")}{" "}
              <Box component="span" sx={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(15,23,42,0.5)" }}>
                bags · {form.varieties.length} {form.varieties.length === 1 ? "variety" : "varieties"}
              </Box>
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button onClick={requestClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", px: 2 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckRoundedIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                px: 3,
                boxShadow: "none",
                background: BRAND_GRADIENT,
                "&:hover": { background: "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)", boxShadow: "none" },
                "&.Mui-disabled": { background: "rgba(15,23,42,0.08)", color: "rgba(15,23,42,0.35)" },
              }}
            >
              {saving ? "Saving..." : "Save report"}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDiscard} onClose={() => setConfirmDiscard(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Discard this report?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "rgba(15,23,42,0.65)", fontSize: "0.9rem" }}>
            You've entered some details that haven't been saved. If you close now, they will be lost.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDiscard(false)} sx={{ textTransform: "none", fontWeight: 600 }}>
            Keep editing
          </Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            onClick={() => {
              setConfirmDiscard(false);
              onClose();
            }}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={3500} onClose={() => setToast("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setToast("")} severity="success" variant="filled" sx={{ borderRadius: "12px", width: "100%" }}>
          {toast}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddManualReportDialog;